import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Card, Table, Button, Spin, Alert, Typography, Divider } from 'antd'
import { CreditCardOutlined, DollarOutlined, BankOutlined } from '@ant-design/icons'
import { env } from '../config/environment'
import {
  PublicPaymentMethodChooser,
  type PublicPaymentMethod,
} from '../components/publicPay/PublicPaymentMethodChooser'
import { calcCardProcessingFee, calcCheckoutTotal } from '../utils/publicPaymentFees'

const { Title, Text } = Typography

const API_ROOT = env.apiBaseUrl.replace(/\/api$/, '') || ''

interface InvoiceSummary {
  id: string
  invoiceNumber: string
  dueAt: string | null
  balanceAmount: number
  totalAmount: number
  status: string
}

export const StatementPayPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [businessName, setBusinessName] = useState<string>('')
  const [invoiceSummaries, setInvoiceSummaries] = useState<InvoiceSummary[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [payLoading, setPayLoading] = useState(false)
  const [creditCardFeePercent, setCreditCardFeePercent] = useState(0)
  const [achEnabled, setAchEnabled] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<PublicPaymentMethod>('card')

  useEffect(() => {
    if (!token) {
      setError('Missing link. Please use the link from your statement email.')
      setLoading(false)
      return
    }
    const fetchData = async () => {
      try {
        setLoading(true)
        setError(null)
        const res = await fetch(`${API_ROOT}/api/public/statement-pay/${token}`)
        const data = await res.json()
        if (!data.success) {
          setError(data.error || 'Invalid or expired link')
          setLoading(false)
          return
        }
        setBusinessName(data.businessName || '')
        const summaries = data.invoiceSummaries || []
        setInvoiceSummaries(summaries)
        setCreditCardFeePercent(
          typeof data.creditCardProcessingFeePercent === 'number'
            ? data.creditCardProcessingFeePercent
            : 0
        )
        const ach = data.achEnabled === true
        setAchEnabled(ach)
        if (!ach) {
          setPaymentMethod('card')
        }
        const ids = (data.invoiceIds || []) as string[]
        setSelectedRowKeys(
          ids.filter((id: string) =>
            summaries.some((s: InvoiceSummary) => s.id === id && s.balanceAmount > 0),
          ),
        )
      } catch (err: any) {
        setError(err.message || 'Failed to load statement')
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [token])

  const onSelectChange = (keys: React.Key[]) => {
    setSelectedRowKeys(keys)
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    getCheckboxProps: (record: InvoiceSummary) => ({
      disabled: record.balanceAmount <= 0,
    }),
  }

  const handlePaySelected = async () => {
    if (selectedRowKeys.length === 0 || !token) {
      return
    }
    setPayLoading(true)
    try {
      const invoiceIds = selectedRowKeys as string[]
      const res = await fetch(`${API_ROOT}/api/public/statement-pay/${token}/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invoiceIds,
          paymentMethod,
          successUrl: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}`,
          cancelUrl: window.location.href,
        }),
      })
      const data = await res.json()
      if (data.success && data.url) {
        window.location.href = data.url
        return
      }
      setError(data.error || 'Failed to start payment')
    } catch (err: any) {
      setError(err.message || 'Failed to start payment')
    } finally {
      setPayLoading(false)
    }
  }

  const totalSelected = invoiceSummaries
    .filter((inv) => selectedRowKeys.includes(inv.id))
    .reduce((sum, inv) => sum + inv.balanceAmount, 0)
  const hasSelection = selectedRowKeys.length > 0 && totalSelected > 0
  const pct = creditCardFeePercent > 0 ? creditCardFeePercent : 0
  const cardFeeEstimate = calcCardProcessingFee(totalSelected, pct)
  const checkoutTotal = calcCheckoutTotal(totalSelected, pct, paymentMethod)

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}
      >
        <Spin size="large" tip="Loading statement..." />
      </div>
    )
  }

  if (error && invoiceSummaries.length === 0) {
    return (
      <div style={{ maxWidth: 600, margin: '48px auto', padding: 24 }}>
        <Alert message="Unable to load statement" description={error} type="error" showIcon />
      </div>
    )
  }

  const columns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Due Date',
      dataIndex: 'dueAt',
      key: 'dueAt',
      render: (dueAt: string | null) => (dueAt ? new Date(dueAt).toLocaleDateString() : '-'),
    },
    {
      title: 'Balance Due',
      dataIndex: 'balanceAmount',
      key: 'balanceAmount',
      align: 'right' as const,
      render: (val: number) => `$${Number(val).toFixed(2)}`,
    },
  ]

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 24 }}>
      <Card>
        <Title level={3} style={{ marginBottom: 8 }}>
          <DollarOutlined /> Pay Invoices
        </Title>
        {businessName && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            {businessName}
          </Text>
        )}

        <Title level={5} style={{ marginTop: 0 }}>
          Step 1 — How will you pay?
        </Title>
        <p style={{ marginBottom: 12, color: '#666' }}>
          Choose card or bank before checkout. Card payments may include a processing fee; bank (ACH)
          does not.
        </p>
        <PublicPaymentMethodChooser
          value={paymentMethod}
          onChange={setPaymentMethod}
          subtotal={totalSelected}
          feePercent={pct}
          achEnabled={achEnabled}
          variant="boxed"
        />

        <Divider />

        <Title level={5}>Step 2 — Select invoices</Title>
        <p style={{ marginBottom: 16 }}>
          Check the invoices you want to pay, then continue to Stripe for a secure{' '}
          {paymentMethod === 'card' ? 'card' : 'bank'} checkout.
        </p>

        {error && (
          <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} closable onClose={() => setError(null)} />
        )}

        <Table
          rowSelection={rowSelection}
          columns={columns}
          dataSource={invoiceSummaries}
          rowKey="id"
          pagination={false}
          size="small"
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Text>Subtotal (selected)</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text>${totalSelected.toFixed(2)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              {paymentMethod === 'card' && pct > 0 && hasSelection && (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={2}>
                    <Text type="secondary">Card processing fee ({pct}%)</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text type="secondary">${cardFeeEstimate.toFixed(2)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Text strong>Total at checkout</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Text strong>${checkoutTotal.toFixed(2)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />

        <Button
          type="primary"
          size="large"
          icon={paymentMethod === 'card' ? <CreditCardOutlined /> : <BankOutlined />}
          onClick={handlePaySelected}
          loading={payLoading}
          disabled={!hasSelection}
          style={{
            marginTop: 24,
            ...(paymentMethod === 'ach'
              ? { background: '#52c41a', borderColor: '#52c41a' }
              : {}),
          }}
        >
          {paymentMethod === 'card'
            ? `Continue to card checkout ($${checkoutTotal.toFixed(2)})`
            : `Continue to bank checkout ($${checkoutTotal.toFixed(2)})`}
        </Button>
      </Card>
    </div>
  )
}
